import React, { useState, useEffect } from 'react';
import { fetchRecentVideos, filterVideosByKeyword, formatDate, formatNumber } from '../utils/youtubeApi';
import './YouTubeVideoSection.css';

function YouTubeVideoSection({ title, subtitle, filterKeyword, includeKeyword = false }) {
  const [videos, setVideos] = useState([]);
  const [filteredVideos, setFilteredVideos] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const videosPerPage = 8;

  useEffect(() => {
    async function loadVideos() {
      setLoading(true);
      try {
        // 최근순으로 30개까지 불러오기
        const allVideos = await fetchRecentVideos(30);
        setVideos(allVideos);
        
        // 키워드로 필터링
        const filtered = filterVideosByKeyword(allVideos, filterKeyword, includeKeyword);
        setFilteredVideos(filtered);
      } catch (error) {
        console.error('Error loading videos:', error);
        setVideos([]);
        setFilteredVideos([]);
      } finally {
        setLoading(false);
      }
    }
    
    loadVideos();
  }, [filterKeyword, includeKeyword]);

  // 페이지네이션 계산
  const totalPages = Math.ceil(filteredVideos.length / videosPerPage);
  const startIndex = (currentPage - 1) * videosPerPage;
  const endIndex = startIndex + videosPerPage;
  const currentVideos = filteredVideos.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    // 페이지네이션 클릭 시 스크롤 이동 제거
  };

  const handleVideoClick = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  if (loading) {
    return (
      <section className={`youtube-section ${includeKeyword ? 'travel-section' : 'music-section'}`}>
        <div className="section-content">
          {title && <h2 className="section-title">{title}</h2>}
          <h3 className="subsection-title">{subtitle}</h3>
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>동영상을 불러오는 중...</p>
            <p className="loading-hint">잠시만 기다려주세요</p>
          </div>
        </div>
      </section>
    );
  }

  if (filteredVideos.length === 0) {
    return (
      <section className={`youtube-section ${includeKeyword ? 'travel-section' : 'music-section'}`}>
        <div className="section-content">
          {title && <h2 className="section-title">{title}</h2>}
          <h3 className="subsection-title">{subtitle}</h3>
          <div className="no-videos">
            <p>동영상을 불러올 수 없습니다.</p>
            <p className="no-videos-hint">
              {videos.length === 0 
                ? '네트워크 연결을 확인하거나 잠시 후 다시 시도해주세요.'
                : `'${filterKeyword}' 키워드로 필터링된 동영상이 없습니다.`}
            </p>
            {videos.length === 0 && (
              <button 
                className="retry-button"
                onClick={() => {
                  setLoading(true);
                  setTimeout(() => {
                    window.location.reload();
                  }, 100);
                }}
              >
                다시 시도
              </button>
            )}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={`youtube-section ${includeKeyword ? 'travel-section' : 'music-section'}`}>
      <div className="section-content">
        {title && <h2 className="section-title">{title}</h2>}
        <h3 className="subsection-title">{subtitle}</h3>
        
        <div className="youtube-videos-grid">
          {currentVideos.map((video) => (
            <div
              key={video.id}
              className="youtube-video-card"
              onClick={() => handleVideoClick(video.url)}
            >
              <div className="video-thumbnail-container">
                <img
                  src={video.thumbnail}
                  alt={video.title}
                  className="video-thumbnail"
                />
                <div className="video-overlay">
                  <div className="play-button">▶</div>
                </div>
              </div>
              <div className="video-info">
                <h4 className="video-title">{video.title}</h4>
                <div className="video-meta">
                  <span className="video-date">
                    📅 {formatDate(video.publishedAt)}
                  </span>
                  {video.viewCount !== null && (
                    <span className="video-stats">
                      👁 {formatNumber(video.viewCount)}
                    </span>
                  )}
                  {video.likeCount !== null && (
                    <span className="video-stats">
                      ❤️ {formatNumber(video.likeCount)}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {totalPages > 1 && (
          <div className="pagination">
            <button
              className="page-btn"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              이전
            </button>
            {[...Array(totalPages)].map((_, index) => {
              const page = index + 1;
              // 현재 페이지 주변 3페이지만 표시
              if (
                page === 1 ||
                page === totalPages ||
                (page >= currentPage - 1 && page <= currentPage + 1)
              ) {
                return (
                  <button
                    key={page}
                    className={`page-btn ${currentPage === page ? 'active' : ''}`}
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </button>
                );
              } else if (page === currentPage - 2 || page === currentPage + 2) {
                return <span key={page} className="page-ellipsis">...</span>;
              }
              return null;
            })}
            <button
              className="page-btn"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              다음
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

export default YouTubeVideoSection;

